import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import useCloneCampaign, {
  TCloneCampaignPayload,
} from '~/features/campaigns/hooks/useCloneCampaign'
import {
  ECampaignAgentAccessScope,
  TCampaign,
  TCampaignError,
} from '~/features/campaigns/models'
import {
  extractFriendlyErrorMessage,
  showToastOnCampaignErrorResponse,
} from '~/features/campaigns/utils'
import { EClientPath } from '~/models/api'
import pathBuilder from '~/utils/api'

type TCloneOfuCampaignParams = {
  campaign: TCampaign
  cloneCampaignName: string
  currentAgentId: string
  isOfu: boolean
  cloneOptions?: {
    agentIds: string[]
    agentAccessScope?: ECampaignAgentAccessScope
    cloneJunctionContent?: boolean
  }
  onSuccess?: (newCampaign: TCampaign) => void
}

export default function useCloneOfuCampaign() {
  const navigate = useNavigate()
  const { mutateAsync: cloneCampaignMutation, isPending: isCloning } =
    useCloneCampaign()

  const cloneCampaign = async ({
    campaign,
    cloneCampaignName,
    currentAgentId,
    isOfu,
    cloneOptions,
    onSuccess,
  }: TCloneOfuCampaignParams) => {
    const trimmedName = cloneCampaignName.trim()

    if (!trimmedName) {
      toast.error('Please enter a campaign name')
      return
    }

    if (!isOfu) {
      toast.error('Cloning is only supported for OFU campaigns')
      return
    }

    const trimmedAgentIds = Array.from(
      new Set(cloneOptions?.agentIds.map(String).map((id) => id.trim()) || []),
    ).filter(Boolean)

    if (!trimmedAgentIds.length) {
      toast.error('Please select at least one assistant')
      return
    }

    const clonePayload: TCloneCampaignPayload = {
      campaignId: campaign.id,
      currentAgentId,
      campaignName: trimmedName,
      agentIds: trimmedAgentIds,
      cloneJunctionContent: cloneOptions?.cloneJunctionContent ?? true,
      ...(cloneOptions?.agentAccessScope
        ? { agentAccessScope: cloneOptions.agentAccessScope }
        : {}),
    }

    try {
      const response = await cloneCampaignMutation(clonePayload)

      if ((response as TCampaignError).error) {
        toast.error(
          `Failed to clone campaign. ${extractFriendlyErrorMessage(
            response as TCampaignError,
          )}`,
        )
        return
      }

      const newCampaign = response as TCampaign

      toast.success('Campaign cloned successfully')
      onSuccess?.(newCampaign)

      navigate(
        pathBuilder(EClientPath.campaignOfuContacts, {
          id: newCampaign.id,
        }),
      )
    } catch (error) {
      showToastOnCampaignErrorResponse(
        error as Error,
        'Failed to clone campaign',
      )
    }
  }

  return {
    cloneCampaign,
    isCloning,
  }
}
